import React, { useEffect, useMemo, useState } from "react";
import Box from "@mui/material/Box";
import Button from "@mui/material/Button";
import Checkbox from "@mui/material/Checkbox";
import Dialog from "@mui/material/Dialog";
import DialogActions from "@mui/material/DialogActions";
import DialogContent from "@mui/material/DialogContent";
import DialogTitle from "@mui/material/DialogTitle";
import FormControlLabel from "@mui/material/FormControlLabel";
import MenuItem from "@mui/material/MenuItem";
import Stack from "@mui/material/Stack";
import TextField from "@mui/material/TextField";
import Typography from "@mui/material/Typography";
import LoadingButton from "@mui/lab/LoadingButton";
import { workspacesApi } from "../../actions";
import { ListSkeleton, Refusal, Surface, dialogPaper, rowSx } from "./WorkspaceUi";
import { fullName } from "./workspaceFormat";

// Who holds a view - and so who reaches its database. This is where access is
// given now: a view carries the one database it was made for, so ticking
// someone here gives them that database, seen through this view and nothing
// else. Unticking takes it away again (the link is marked removed, never
// deleted, so the history stays).
export default function ViewPeopleDialog({ open, workspace, view, mayChange, onClose, onSaved }) {
  const [people, setPeople] = useState(null);
  const [chosen, setChosen] = useState([]);
  const [mode, setMode] = useState("view");
  const [error, setError] = useState("");
  const [saving, setSaving] = useState(false);

  const members = useMemo(() => people || [], [people]);

  useEffect(() => {
    if (!open || !view) return undefined;
    let live = true;
    setPeople(null);
    setChosen([]);
    setError("");
    (async () => {
      const [all, holders] = await Promise.all([workspacesApi.users(workspace.id), workspacesApi.viewPeople(workspace.id, view.id)]);
      if (!live) return;
      if (!all.success) {
        setError(all.msg || "Couldn't load the people in this workspace.");
        setPeople([]);
        return;
      }
      setPeople(all.content?.users || []);
      if (holders.success) {
        const held = holders.content || [];
        setChosen(held.map((person) => person.id));
        if (held.some((person) => person.mode === "edit")) setMode("edit");
      } else setError(holders.msg || "Couldn't read who holds this view.");
    })();
    return () => {
      live = false;
    };
  }, [open, workspace.id, view]);

  const toggle = (id) => setChosen((current) => (current.includes(id) ? current.filter((one) => one !== id) : [...current, id]));

  const save = async () => {
    setSaving(true);
    setError("");
    const res = await workspacesApi.setViewPeople(workspace.id, view.id, chosen, mode);
    setSaving(false);
    if (!res.success) {
      setError(res.msg || "Couldn't save who holds this view.");
      return;
    }
    if (onSaved) await onSaved(res.msg || "Saved.");
    onClose();
  };

  const where = view?.databaseLabel || view?.database || "";

  return (
    <Dialog open={open} onClose={saving ? undefined : onClose} fullWidth maxWidth="sm" PaperProps={dialogPaper}>
      <DialogTitle sx={{ fontWeight: 700, pb: 0.5 }}>Who sees {view?.name}</DialogTitle>
      <DialogContent>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
          {where
            ? `Everyone ticked here reaches ${where}, and sees it through this view. Nobody else does.`
            : "This view isn't tied to a database yet, so it shows nobody anything. Give it one first."}
        </Typography>

        {error && <Refusal sx={{ mb: 2 }}>{error}</Refusal>}
        {!people && !error && <ListSkeleton rows={3} height={48} />}

        {people && (
          <Stack spacing={2}>
            <TextField
              select
              size="small"
              label="They may"
              value={mode}
              disabled={!mayChange}
              onChange={(e) => setMode(e.target.value)}
              helperText="Edit also lets them change content in this database, where the plan allows it."
              sx={{ maxWidth: 280 }}
            >
              <MenuItem value="view">Read only</MenuItem>
              <MenuItem value="edit">Read and edit content</MenuItem>
            </TextField>

            <Surface>
              {members.length === 0 ? (
                <Box sx={rowSx(true)}>
                  <Typography variant="body2" color="text.secondary">
                    Nobody is in this workspace yet. Invite someone first.
                  </Typography>
                </Box>
              ) : (
                members.map((person, index) => (
                  <Box key={person.id} sx={rowSx(!index)}>
                    <FormControlLabel
                      control={<Checkbox checked={chosen.includes(person.id)} disabled={!mayChange} onChange={() => toggle(person.id)} />}
                      label={
                        <Box>
                          <Typography sx={{ fontWeight: 600 }}>{fullName(person)}</Typography>
                          <Typography variant="caption" color="text.secondary">
                            {person.email}
                          </Typography>
                        </Box>
                      }
                    />
                  </Box>
                ))
              )}
            </Surface>
          </Stack>
        )}
      </DialogContent>
      <DialogActions sx={{ px: 3, pb: 2.5 }}>
        <Button onClick={onClose} disabled={saving}>
          {mayChange ? "Cancel" : "Close"}
        </Button>
        {mayChange && (
          <LoadingButton variant="contained" loading={saving} onClick={save}>
            Save
          </LoadingButton>
        )}
      </DialogActions>
    </Dialog>
  );
}
